import { type Key, useCallback, useMemo } from 'react';
import { createGlobalStyle } from 'styled-components';

import { CSS_PREFIX } from '@/constants/styles';

import { ROW_HIGHLIGHTED_CLASS } from '../../constants';
import type { HighlightedRows } from '../../types';
import { getRowKey, isHighlightedRowObject } from '../../utils';

import { sanitizeColor, sanitizeCssClassName } from './utils';

type Props = {
  highlightedRows?: HighlightedRows;
  rowKey?: string | ((record: unknown) => Key);
};

const useHighlightedRows = ({ highlightedRows, rowKey }: Props) => {
  const highlightMap = useMemo(() => {
    if (!highlightedRows?.length) return new Map<Key, string | undefined>();

    return new Map(
      highlightedRows.map(item => {
        const key = getRowKey(item);
        const color = isHighlightedRowObject(item)
          ? sanitizeColor(item.color)
          : undefined;
        return [key, color];
      })
    );
  }, [highlightedRows]);

  const getRowClassName = useCallback(
    (record: unknown) => {
      if (!highlightMap.size) return '';

      const key =
        typeof rowKey === 'function'
          ? rowKey(record)
          : (record as Record<string, Key>)?.[rowKey ?? 'key'];

      if (!highlightMap.has(key)) return '';

      const color = highlightMap.get(key);
      const safeKey = sanitizeCssClassName(key);

      return color
        ? `${ROW_HIGHLIGHTED_CLASS} ${ROW_HIGHLIGHTED_CLASS}-${safeKey}`
        : ROW_HIGHLIGHTED_CLASS;
    },
    [highlightMap, rowKey]
  );

  const HighlightStyle = useMemo(() => {
    const customColorEntries = Array.from(highlightMap.entries()).filter(
      ([, color]) => color !== undefined
    );

    if (!customColorEntries.length) return null;

    const rules = customColorEntries
      .map(
        ([key, color]) =>
          `.${ROW_HIGHLIGHTED_CLASS}-${sanitizeCssClassName(key)} .${CSS_PREFIX}-table-cell { background-color: ${color} !important; }`
      )
      .join('\n');

    return createGlobalStyle`${rules}`;
  }, [highlightMap]);

  return {
    getRowClassName: highlightedRows?.length ? getRowClassName : undefined,
    HighlightStyle
  };
};

export default useHighlightedRows;
